import React, { useState } from 'react';
import { 
  Package, 
  Search, 
  Filter, 
  Plus, 
  Eye, 
  Edit, 
  AlertTriangle,
  CheckCircle,
  Clock,
  TrendingUp,
  TrendingDown,
  Calendar,
  DollarSign,
  MapPin,
  Truck,
  BarChart3
} from 'lucide-react';

interface InventoryItem {
  id: string;
  name: string;
  genericName: string;
  ndc: string;
  strength: string;
  dosageForm: string;
  manufacturer: string;
  controlled: boolean;
  schedule: string;
  quantity: number;
  unitCost: number;
  sellingPrice: number;
  expirationDate: string;
  lotNumber: string;
  location: string;
  minStockLevel: number;
  maxStockLevel: number;
  reorderPoint: number;
  supplier: string;
  lastOrderDate: string;
  nextOrderDate: string;
  category: string;
  therapeuticClass: string;
  status: 'good' | 'low' | 'out' | 'overstock' | 'expiring';
}

export function Inventory() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedLocation, setSelectedLocation] = useState<string>('all');

  const mockInventory: InventoryItem[] = [
    {
      id: '1',
      name: 'Lisinopril',
      genericName: 'Lisinopril',
      ndc: '00071-0400-01',
      strength: '10mg',
      dosageForm: 'tablet',
      manufacturer: 'Merck',
      controlled: false,
      schedule: '',
      quantity: 150,
      unitCost: 0.15,
      sellingPrice: 0.50,
      expirationDate: '2026-12-31',
      lotNumber: 'LOT123456',
      location: 'Main Shelf A1',
      minStockLevel: 100,
      maxStockLevel: 500,
      reorderPoint: 150,
      supplier: 'Cardinal Health',
      lastOrderDate: '2024-08-01',
      nextOrderDate: '2024-09-01',
      category: 'Cardiovascular',
      therapeuticClass: 'ACE Inhibitor',
      status: 'good'
    },
    {
      id: '2',
      name: 'Metformin',
      genericName: 'Metformin HCl',
      ndc: '00071-0500-01',
      strength: '500mg',
      dosageForm: 'tablet',
      manufacturer: 'Teva',
      controlled: false,
      schedule: '',
      quantity: 75,
      unitCost: 0.08,
      sellingPrice: 0.30,
      expirationDate: '2025-06-30',
      lotNumber: 'LOT789012',
      location: 'Main Shelf B2',
      minStockLevel: 100,
      maxStockLevel: 400,
      reorderPoint: 120,
      supplier: 'McKesson',
      lastOrderDate: '2024-07-15',
      nextOrderDate: '2024-08-15',
      category: 'Diabetes',
      therapeuticClass: 'Biguanide',
      status: 'low'
    },
    {
      id: '3',
      name: 'Albuterol HFA',
      genericName: 'Albuterol Sulfate',
      ndc: '00071-0900-01',
      strength: '90mcg',
      dosageForm: 'inhaler',
      manufacturer: 'GSK',
      controlled: false,
      schedule: '',
      quantity: 0,
      unitCost: 8.50,
      sellingPrice: 45.00,
      expirationDate: '2025-03-31',
      lotNumber: 'LOT345678',
      location: 'Refrigerated C1',
      minStockLevel: 20,
      maxStockLevel: 100,
      reorderPoint: 25,
      supplier: 'Cardinal Health',
      lastOrderDate: '2024-08-10',
      nextOrderDate: '2024-08-20',
      category: 'Respiratory',
      therapeuticClass: 'Beta-2 Agonist',
      status: 'out'
    },
    {
      id: '4',
      name: 'Oxycodone',
      genericName: 'Oxycodone HCl',
      ndc: '00071-0300-01',
      strength: '5mg',
      dosageForm: 'tablet',
      manufacturer: 'Mallinckrodt',
      controlled: true,
      schedule: 'CII',
      quantity: 600,
      unitCost: 0.25,
      sellingPrice: 1.00,
      expirationDate: '2027-01-31',
      lotNumber: 'LOT901234',
      location: 'Controlled Vault D1',
      minStockLevel: 200,
      maxStockLevel: 800,
      reorderPoint: 300,
      supplier: 'Cardinal Health',
      lastOrderDate: '2024-07-01',
      nextOrderDate: '2024-10-01',
      category: 'Pain Management',
      therapeuticClass: 'Opioid',
      status: 'overstock'
    }
  ];

  const filteredInventory = mockInventory.filter(item => {
    const matchesSearch = 
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.genericName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.ndc.includes(searchTerm) ||
      item.manufacturer.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = selectedStatus === 'all' || item.status === selectedStatus;
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesLocation = selectedLocation === 'all' || item.location === selectedLocation;
    
    return matchesSearch && matchesStatus && matchesCategory && matchesLocation;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'good': return 'bg-green-100 text-green-800 border-green-200';
      case 'low': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'out': return 'bg-red-100 text-red-800 border-red-200';
      case 'overstock': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'expiring': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'good': return <CheckCircle className="w-4 h-4" />;
      case 'low': return <AlertTriangle className="w-4 h-4" />;
      case 'out': return <AlertTriangle className="w-4 h-4" />;
      case 'overstock': return <TrendingUp className="w-4 h-4" />;
      case 'expiring': return <Clock className="w-4 h-4" />;
      default: return <CheckCircle className="w-4 h-4" />;
    }
  };

  const getDaysUntilExpiration = (expirationDate: string) => {
    const today = new Date();
    const expiration = new Date(expirationDate);
    const diffTime = expiration.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getExpirationColor = (days: number) => {
    if (days < 30) return 'text-red-600';
    if (days < 90) return 'text-orange-600';
    if (days < 180) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getStockStatus = (item: InventoryItem) => {
    if (item.quantity === 0) return 'out';
    if (item.quantity <= item.reorderPoint) return 'low';
    if (item.quantity > item.maxStockLevel) return 'overstock';
    if (getDaysUntilExpiration(item.expirationDate) < 90) return 'expiring';
    return 'good';
  };

  const getTotalValue = () => {
    return mockInventory.reduce((total, item) => total + (item.quantity * item.unitCost), 0);
  };

  const getLowStockCount = () => {
    return mockInventory.filter(item => getStockStatus(item) === 'low').length;
  };

  const getExpiringCount = () => {
    return mockInventory.filter(item => getDaysUntilExpiration(item.expirationDate) < 90).length;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-gray-900">Inventory Management</h1>
          <p className="text-gray-600 mt-2">Monitor stock levels, track expiration dates, and manage reorders</p>
        </div>
        <button className="bg-gradient-to-r from-[#4B0082] to-[#6B38FB] hover:from-[#6B38FB] hover:to-[#4B0082] text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:scale-105 shadow-lg">
          <Plus className="w-5 h-5 inline mr-2" />
          Add Item
        </button>
      </div>

      {/* Inventory Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Items</p>
              <p className="text-3xl font-bold text-gray-900">{mockInventory.length}</p>
            </div>
            <div className="p-3 bg-[#6B38FB]/10 rounded-xl">
              <Package className="w-8 h-8 text-[#6B38FB]" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Value</p>
              <p className="text-3xl font-bold text-gray-900">${getTotalValue().toFixed(2)}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-xl">
              <DollarSign className="w-8 h-8 text-green-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Low Stock</p>
              <p className="text-3xl font-bold text-yellow-600">{getLowStockCount()}</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-xl">
              <AlertTriangle className="w-8 h-8 text-yellow-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Expiring Soon</p>
              <p className="text-3xl font-bold text-orange-600">{getExpiringCount()}</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-xl">
              <Clock className="w-8 h-8 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Search Inventory</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, NDC, or manufacturer..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6B38FB] focus:border-transparent"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Stock Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6B38FB] focus:border-transparent"
            >
              <option value="all">All Statuses</option>
              <option value="good">Good</option>
              <option value="low">Low Stock</option>
              <option value="out">Out of Stock</option>
              <option value="overstock">Overstock</option>
              <option value="expiring">Expiring Soon</option>
            </select>
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6B38FB] focus:border-transparent"
            >
              <option value="all">All Categories</option>
              <option value="Cardiovascular">Cardiovascular</option>
              <option value="Diabetes">Diabetes</option>
              <option value="Respiratory">Respiratory</option>
              <option value="Pain Management">Pain Management</option>
            </select>
          </div>
        </div>
      </div>

      {/* Inventory List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Item</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Stock</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Location</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Expiration</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Value</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredInventory.map((item) => {
                const stockStatus = getStockStatus(item);
                const daysUntilExpiration = getDaysUntilExpiration(item.expirationDate);
                
                return (
                  <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-gradient-to-br from-[#4B0082] to-[#6B38FB] rounded-full flex items-center justify-center text-white">
                            <Package className="w-5 h-5" />
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-semibold text-gray-900">{item.name}</p>
                            <p className="text-sm text-gray-500">{item.genericName} {item.strength} {item.dosageForm}</p>
                            <p className="text-sm text-gray-500">NDC: {item.ndc}</p>
                            {item.controlled && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                                C{item.schedule}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(stockStatus)}`}>
                            {getStatusIcon(stockStatus)}
                            <span className="ml-1 capitalize">{stockStatus}</span>
                          </span>
                        </div>
                        <p className="text-sm font-medium text-gray-900 mt-1">{item.quantity} units</p>
                        <p className="text-xs text-gray-500">Reorder: {item.reorderPoint}</p>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                          <div 
                            className={`h-2 rounded-full ${
                              stockStatus === 'out' ? 'bg-red-500' :
                              stockStatus === 'low' ? 'bg-yellow-500' :
                              stockStatus === 'overstock' ? 'bg-blue-500' :
                              'bg-green-500'
                            }`}
                            style={{ 
                              width: `${Math.min((item.quantity / item.maxStockLevel) * 100, 100)}%` 
                            }}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center text-sm text-gray-900">
                        <MapPin className="w-4 h-4 mr-2 text-[#6B38FB]" />
                        {item.location}
                      </div>
                      <p className="text-sm text-gray-500">{item.manufacturer}</p>
                      <p className="text-sm text-gray-500">Lot: {item.lotNumber}</p>
                    </td>
                    <td className="px-6 py-4">
                      <div className={`text-sm font-medium ${getExpirationColor(daysUntilExpiration)}`}>
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-2" />
                          {new Date(item.expirationDate).toLocaleDateString()}
                        </div>
                        <p className="mt-1">
                          {daysUntilExpiration > 0 
                            ? `${daysUntilExpiration} days remaining`
                            : 'Expired'
                          }
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">${(item.quantity * item.unitCost).toFixed(2)}</p>
                        <p className="text-sm text-gray-500">Unit: ${item.unitCost.toFixed(2)}</p>
                        <p className="text-sm text-gray-500">Sell: ${item.sellingPrice.toFixed(2)}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button className="p-2 text-[#6B38FB] hover:bg-[#6B38FB]/10 rounded-lg transition-colors">
                          <Eye className="w-4 h-4" />
                        </button>
                        <button className="p-2 text-[#6B38FB] hover:bg-[#6B38FB]/10 rounded-lg transition-colors">
                          <Edit className="w-4 h-4" />
                        </button>
                        {stockStatus === 'low' || stockStatus === 'out' ? (
                          <button className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors">
                            <Truck className="w-4 h-4" />
                          </button>
                        ) : null}
                        <button className="p-2 text-[#6B38FB] hover:bg-[#6B38FB]/10 rounded-lg transition-colors">
                          <BarChart3 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
